import { memo, Fragment, FocusEvent, ClipboardEvent } from 'react'
import {
  Button,
  Hidden,
  Stack,
  Text,
  Checkbox
} from '@leroy-merlin-br/backyard-react'
import { PasswordField } from 'user/signup/components'
import { Popover } from 'user/components'
import { hasAccountFiscalId } from 'user/signup/utils'
import { handlePhoneFormat, birthdateErrorMessage } from 'user/utils'

import { TextField, SelectField, NumberField } from 'shared/components'

import * as validator from 'utils/validators/validators'

import { DocumentPro } from 'lmcv/components'

import { PROFESSIONS } from './constants'
import { useFormPF } from './useFormPF'
import NewsInfo from '../NewsInfo'

const FormPF = () => {
  const {
    clearErrors,
    control,
    register,
    setError,
    handleSubmit,
    watch,
    isSubmitting,
    classOccupation,
    stores,
    isProfessionalWatch,
    onFormSubmit,
    onFormInvalid,
    onSignupCompleted,
    errors
  } = useFormPF()

  return (
    <Fragment>
      <DocumentPro
        open={classOccupation !== ''}
        onCancel={onSignupCompleted}
        onFinish={onSignupCompleted}
        classOccupation={classOccupation}
      />
      <form onSubmit={handleSubmit(onFormSubmit, onFormInvalid)} data-cy="signup-form">
        <Stack space="mega">
          <Hidden when={['giga', 'tera']}>
            <Text size="giga" isBold noMargin>
              Faça seu cadastro e transforme as suas compras em{' '}
              <Text as="span" size="giga" color="v500" isBold>
                pontos
              </Text>{' '}
              e benefícios.
            </Text>
          </Hidden>
          <NumberField
            name="fiscalId"
            label={
              <Popover
                list={{
                  title: 'Seu CPF será usado para:',
                  items: [
                    'Te identificar',
                    'Acessar sua conta',
                    'Consultar pedidos',
                    'Analisar crédito',
                    'Realizar transações financeiras'
                  ]
                }}
              >
                CPF
              </Popover>
            }
            rules={{
              required: 'Insira seu CPF',
              validate: (value: string) => validator.isCpf(value) || 'CPF inválido'
            }}
            control={control}
            state={Boolean(errors.fiscalId) && 'invalid'}
            hint={hasAccountFiscalId(errors, 'CPF')}
            format="###.###.###-##"
          />
          <TextField
            name="name"
            label={
              <Popover
                list={{
                  title: 'Seu Nome será usado para:',
                  items: ['Te identificar', 'Saber como podemos te chamar']
                }}
                info="Se você preferir, pode informar seu nome social com sobrenome, ok?"
              >
                Nome completo
              </Popover>
            }
            rules={{
              required: 'Insira seu nome',
              validate: (value: string) => validator.isValidName(value) || 'Nome inválido'
            }}
            control={control}
            state={Boolean(errors.name) && 'invalid'}
            hint={errors.name?.message}
          />
          <NumberField
            name="loyalty.birthdate"
            label={
              <Popover
                list={{
                  title: 'Sua data de nascimento será usada para:',
                  items: ['Envio de mimos no dia do seu aniversário :)']
                }}
              >
                Data de nascimento
              </Popover>
            }
            rules={{
              required: 'Insira sua data de nascimento',
              validate: (value: string) => birthdateErrorMessage(value)
            }}
            control={control}
            state={Boolean(errors.loyalty?.birthdate) && 'invalid'}
            hint={errors.loyalty?.birthdate?.message}
            format="##/##/####"
            valueType="formatted"
          />
          <SelectField
            name="loyalty.favoriteStore"
            label={
              <Popover
                list={{
                  title: 'Sua loja favorita será usada para:',
                  items: [
                    'Envio de ofertas e conteúdo exclusivos da loja escolhida'
                  ]
                }}
              >
                Loja favorita
              </Popover>
            }
            options={stores}
            rules={{
              required: 'Escolha sua loja favorita'
            }}
            control={control}
            state={Boolean(errors.loyalty?.favoriteStore) && 'invalid'}
            hint={errors.loyalty?.favoriteStore?.message}
          />
          <TextField
            name="email"
            type="email"
            label={
              <Popover
                list={{
                  title: 'Seu e-mail será usado para:',
                  items: [
                    'Te identificar',
                    'Acessar sua conta',
                    'Receber sua nota fiscal',
                    'Receber o status do seu pedido'
                  ]
                }}
              >
                E-mail
              </Popover>
            }
            rules={{
              required: 'Insira seu e-mail',
              validate: (value: string) => validator.isEmail(value) || 'E-mail inválido'
            }}
            control={control}
            autoComplete="new-password"
            state={Boolean(errors.email) && 'invalid'}
            hint={errors.email?.message}
          />
          {Boolean(watch('email')) && (
            <TextField
              name="emailConfirm"
              type="email"
              label="Confirmar e-mail"
              rules={{
                required: 'Insira seu e-mail',
                validate: (value: string) =>
                  value === watch('email') || 'Os e-mail precisam ser iguais'
              }}
              onPaste={(e: ClipboardEvent<HTMLInputElement>) => {
                e.preventDefault()

                return false
              }}
              control={control}
              autoComplete="new-password"
              state={Boolean(errors.emailConfirm) && 'invalid'}
              hint={errors.emailConfirm?.message}
            />
          )}
          <NumberField
            name="phone"
            label={
              <Popover
                list={{
                  title: 'Seu celular será usado para:',
                  items: [
                    'Garantir mais segurança para sua conta',
                    'Contato rápido para informar sobre seu pedido',
                    'Envio de novidades e ofertas, se você aceitar :)'
                  ]
                }}
              >
                Celular
              </Popover>
            }
            rules={{
              required: 'Insira seu Celular',
              validate: (value: string) => validator.isCellPhone(value) || 'Celular inválido'
            }}
            control={control}
            state={Boolean(errors.phone) && 'invalid'}
            hint={errors.phone?.message}
            format={handlePhoneFormat}
            onBlur={({ target }: FocusEvent<HTMLInputElement>) => {
              const rawVal = target.value.replace(/[^\d]/g, '')

              if (!rawVal.length) {
                return setError('phone', {
                  type: 'manual',
                  message: 'Insira seu Celular'
                })
              }

              if (rawVal.length >= 3 && !validator.isCellPhoneDigit(rawVal)) {
                return setError('phone', {
                  type: 'manual',
                  message: 'O telefone deve ser um celular'
                })
              }

              if (!validator.isCellPhone(rawVal)) {
                return setError('phone', {
                  type: 'manual',
                  message: 'Celular inválido'
                })
              }

              clearErrors('phone')
            }}
          />
          <PasswordField
            name="password"
            label="Senha"
            rules={{
              required: 'Insira sua senha'
            }}
            control={control}
            autoComplete="new-password"
            state={Boolean(errors.password) && 'invalid'}
            hint={errors.password?.message}
          />

          <Checkbox
            {...register('loyalty.optInPro')}
            hint="Profissionais das áreas de engenharia, arquitetura, decoração, ou mesmo quem atua como pedreiro ou pedreira, etc tem vantagens únicas como carteira de clientes e acesso à eventos."
          >
            Sou profissional da construção civil
          </Checkbox>

          {isProfessionalWatch && (
            <SelectField
              name="loyalty.occupation"
              label="Qual sua profissão?"
              options={PROFESSIONS}
              rules={{
                required: 'Escolha sua profissão'
              }}
              control={control}
              state={Boolean(errors.loyalty?.occupation) && 'invalid'}
              hint={errors.loyalty?.occupation?.message}
            />
          )}

          <NewsInfo />

          <Button isStretch type="submit" isLoading={isSubmitting}>
            Concordar e criar conta
          </Button>
        </Stack>
      </form>
    </Fragment>
  )
}

export default memo(FormPF)
